/**
 * Weather File Library Component
 *
 * Main component for browsing and selecting weather files from the global library.
 * Weather data is loaded asynchronously from JSON files.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type {
  WeatherFileMetadata,
  WeatherFileSearchQuery,
  WeatherFileSearchResult,
  ASHRAEClimateZone,
  WeatherFileSourceType,
} from '../../types';
import {
  searchWeatherFilesWithFacets,
  getNearbyWeatherFiles,
  getWeatherManifest,
  type FilterFacetCounts,
} from '../../services/weatherFileSearch';
import { useWeatherFilterStore } from '../../weatherFilterStore';
import { WeatherFileSearch } from './WeatherFileSearch';
import { WeatherFileList } from './WeatherFileList';
import { WeatherFilters, type PendingFilters } from './WeatherFilters';
import { ClimateZoneGrid } from './ClimateZoneGrid';
import { WeatherFilePreview } from './WeatherFilePreview';
import { WeatherFileMap } from './WeatherFileMap';
import { useIsMobile } from '../../../hooks';

export interface WeatherFileLibraryProps {
  onSelect?: (file: WeatherFileMetadata) => void;
  onClose?: () => void;
  initialQuery?: WeatherFileSearchQuery;
  /** ID of currently selected file for highlighting */
  selectedFileId?: string | undefined;
}

export function WeatherFileLibrary({
  onSelect,
  onClose,
  initialQuery,
  selectedFileId,
}: WeatherFileLibraryProps) {
  const isMobile = useIsMobile();

  // Use persisted filter state from store
  const {
    searchText,
    selectedCountry,
    selectedClimateZones,
    selectedSourceTypes,
    selectedYearRanges,
    viewMode,
    setSearchText,
    setSelectedCountry,
    setSelectedClimateZones,
    setSelectedSourceTypes,
    setSelectedYearRanges,
    setViewMode,
    clearAllFilters,
  } = useWeatherFilterStore();

  // Filter popover/drawer state
  const [showFilters, setShowFilters] = useState(false);
  const filterPopoverRef = useRef<HTMLDivElement>(null);

  // Apply initial query if provided (one-time initialization)
  useEffect(() => {
    if (initialQuery) {
      if (initialQuery.text) setSearchText(initialQuery.text);
      if (initialQuery.countryCode) setSelectedCountry(initialQuery.countryCode);
      if (initialQuery.climateZone) {
        setSelectedClimateZones(
          Array.isArray(initialQuery.climateZone)
            ? initialQuery.climateZone
            : [initialQuery.climateZone]
        );
      }
      if (initialQuery.sourceType) {
        setSelectedSourceTypes(
          Array.isArray(initialQuery.sourceType)
            ? initialQuery.sourceType
            : [initialQuery.sourceType]
        );
      }
      if (initialQuery.yearRange) {
        setSelectedYearRanges(
          Array.isArray(initialQuery.yearRange)
            ? initialQuery.yearRange
            : [initialQuery.yearRange]
        );
      }
    }
    // Only run once on mount with initialQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pending filter state for facet count calculation
  const [pendingFilters, setPendingFilters] = useState<PendingFilters | null>(null);
  // Result count preview for pending filters
  const [pendingResultCount, setPendingResultCount] = useState<number | undefined>(undefined);

  // Preview state
  const [previewFile, setPreviewFile] = useState<WeatherFileMetadata | null>(null);

  // Loading states
  const [, setIsLoadingCountries] = useState(true);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Track when showing nearby/geolocation results (to prevent useEffect from clearing them)
  const [isShowingNearbyResults, setIsShowingNearbyResults] = useState(false);

  // Loaded data
  const [countries, setCountries] = useState<{ code: string; name: string; count: number }[]>([]);
  const [manifestClimateZones, setManifestClimateZones] = useState<{ zone: string; count: number }[]>([]);
  const [manifestSourceTypes, setManifestSourceTypes] = useState<{ type: string; count: number }[]>([]);
  const [manifestYearRanges, setManifestYearRanges] = useState<{ yearRange: string; count: number }[]>([]);
  const [searchResults, setSearchResults] = useState<WeatherFileSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [dynamicFacets, setDynamicFacets] = useState<FilterFacetCounts | null>(null);

  // Recent files loaded from localStorage
  const [recentFiles, setRecentFiles] = useState<WeatherFileMetadata[]>([]);

  // Load recent files on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('weather-recent-files');
      if (stored) {
        const files = JSON.parse(stored) as WeatherFileMetadata[];
        setRecentFiles(files.slice(0, 5)); // Show up to 5 recent files
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Scroll container ref for infinite scrolling
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Page size for infinite scrolling
  const PAGE_SIZE = 50;

  // Load manifest (countries and aggregate counts) on mount
  useEffect(() => {
    let mounted = true;
    setIsLoadingCountries(true);

    getWeatherManifest()
      .then((manifest) => {
        if (mounted) {
          setCountries(manifest.countries);
          setManifestClimateZones(manifest.climateZones || []);
          setManifestSourceTypes(manifest.sourceTypes || []);
          setManifestYearRanges(manifest.yearRanges || []);
          setIsLoadingCountries(false);
        }
      })
      .catch((error) => {
        console.error('Failed to load manifest:', error);
        if (mounted) {
          setIsLoadingCountries(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Close filter popover when clicking outside (desktop only)
  useEffect(() => {
    if (!showFilters || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopoverRef.current && !filterPopoverRef.current.contains(event.target as Node)) {
        setShowFilters(false);
        setPendingFilters(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters, isMobile]);

  // Build search query for results (uses applied filters)
  const searchQuery = useMemo<WeatherFileSearchQuery>(() => {
    const query: WeatherFileSearchQuery = {};

    if (searchText.trim()) {
      query.text = searchText;
    }

    if (selectedCountry) {
      query.countryCode = selectedCountry;
    }

    if (selectedClimateZones.length > 0) {
      query.climateZone = selectedClimateZones;
    }

    if (selectedSourceTypes.length > 0) {
      query.sourceType = selectedSourceTypes;
    }

    if (selectedYearRanges.length > 0) {
      query.yearRange = selectedYearRanges;
    }

    return query;
  }, [searchText, selectedCountry, selectedClimateZones, selectedSourceTypes, selectedYearRanges]);

  // Build query for facet counts (uses pending filters when filter popover is open)
  const facetQuery = useMemo<WeatherFileSearchQuery>(() => {
    // When filter popover is open, use pending filters for facet calculation
    if (showFilters && pendingFilters) {
      const query: WeatherFileSearchQuery = {};

      if (searchText.trim()) {
        query.text = searchText;
      }

      if (pendingFilters.country) {
        query.countryCode = pendingFilters.country;
      }

      if (pendingFilters.climateZones.length > 0) {
        query.climateZone = pendingFilters.climateZones;
      }

      if (pendingFilters.sourceTypes.length > 0) {
        query.sourceType = pendingFilters.sourceTypes;
      }

      if (pendingFilters.yearRanges.length > 0) {
        query.yearRange = pendingFilters.yearRanges;
      }

      return query;
    }

    // Otherwise use applied filters
    return searchQuery;
  }, [showFilters, pendingFilters, searchText, searchQuery]);

  // Track if we should skip loading (no filters = show prompt instead)
  const hasFilters = searchText.trim() !== '' || selectedCountry !== null ||
    selectedClimateZones.length > 0 || selectedSourceTypes.length > 0 || selectedYearRanges.length > 0;

  // Execute search when query changes
  useEffect(() => {
    // If no filters and not showing nearby results, don't load all data - just show empty with prompt
    if (!hasFilters) {
      // Don't clear results if we're showing nearby/geolocation results
      if (!isShowingNearbyResults) {
        setSearchResults([]);
        setTotalCount(0);
        setHasMore(false);
        setDynamicFacets(null);
        setIsLoadingResults(false);
      }
      return;
    }

    // If user starts filtering, clear the nearby results flag
    if (isShowingNearbyResults) {
      setIsShowingNearbyResults(false);
    }

    let mounted = true;
    setIsLoadingResults(true);

    searchWeatherFilesWithFacets({ ...searchQuery, limit: PAGE_SIZE, offset: 0 })
      .then((response) => {
        if (mounted) {
          setSearchResults(response.results);
          setTotalCount(response.totalCount);
          setHasMore(response.hasMore);
          // Only update facets if filter popover is not showing pending changes
          if (!showFilters || !pendingFilters) {
            setDynamicFacets(response.facets);
          }
          setIsLoadingResults(false);
        }
      })
      .catch((error) => {
        console.error('Search failed:', error);
        if (mounted) {
          setSearchResults([]);
          setTotalCount(0);
          setHasMore(false);
          if (!showFilters || !pendingFilters) {
            setDynamicFacets(null);
          }
          setIsLoadingResults(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [searchQuery, hasFilters, showFilters, pendingFilters, isShowingNearbyResults]);

  // Fetch facet counts based on pending filters (for live count updates in filter popover)
  useEffect(() => {
    if (!showFilters || !pendingFilters) {
      setPendingResultCount(undefined);
      return;
    }

    // Build a query to get facets for pending filters
    const hasPendingFilters = pendingFilters.country !== null ||
      pendingFilters.climateZones.length > 0 ||
      pendingFilters.sourceTypes.length > 0 ||
      pendingFilters.yearRanges.length > 0 ||
      searchText.trim() !== '';

    if (!hasPendingFilters) {
      // No pending filters - show manifest counts
      setDynamicFacets(null);
      setPendingResultCount(undefined);
      return;
    }

    let mounted = true;

    searchWeatherFilesWithFacets({ ...facetQuery, limit: 1, offset: 0 })
      .then((response) => {
        if (mounted) {
          setDynamicFacets(response.facets);
          setPendingResultCount(response.totalCount);
        }
      })
      .catch((error) => {
        console.error('Facet fetch failed:', error);
      });

    return () => {
      mounted = false;
    };
  }, [facetQuery, showFilters, pendingFilters, searchText]);

  // Load more results for infinite scrolling
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const response = await searchWeatherFilesWithFacets({
        ...searchQuery,
        limit: PAGE_SIZE,
        offset: searchResults.length,
      });
      setSearchResults((prev) => [...prev, ...response.results]);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [searchQuery, searchResults.length, isLoadingMore, hasMore]);

  // Handle scroll for infinite scrolling
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls within 200px of bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  }, [loadMore, isLoadingMore, hasMore]);

  // Handle file selection - also update recent files for immediate UI feedback
  const handleSelect = useCallback(
    (file: WeatherFileMetadata) => {
      // Update local recent files for immediate feedback
      setRecentFiles((prev) => {
        const filtered = prev.filter((f) => f.id !== file.id);
        return [file, ...filtered].slice(0, 5);
      });
      onSelect?.(file);
    },
    [onSelect]
  );

  // Handle preview - show modal immediately, climate data loads lazily in preview
  const handlePreview = useCallback((file: WeatherFileMetadata) => {
    setPreviewFile(file);
  }, []);

  // Handle geolocation
  const handleUseLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const nearbyFiles = await getNearbyWeatherFiles(
            position.coords.latitude,
            position.coords.longitude,
            20
          );
          setIsLocating(false);

          if (nearbyFiles.length > 0) {
            // Show nearby files - set flag BEFORE clearing filters to prevent useEffect from wiping results
            setIsShowingNearbyResults(true);
            setSearchResults(nearbyFiles);
            setTotalCount(nearbyFiles.length);
            setHasMore(false);
            // Clear all filters using the store
            clearAllFilters();
          }
        } catch (error) {
          console.error('Failed to get nearby files:', error);
          setIsLocating(false);
          setLocationError('Failed to find nearby weather files');
        }
      },
      (error) => {
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('An unknown error occurred');
        }
      },
      { timeout: 10000 }
    );
  }, [clearAllFilters]);

  // Handle climate zone selection from grid
  const handleClimateZoneSelect = useCallback((zone: ASHRAEClimateZone) => {
    setSelectedClimateZones([zone]);
    setViewMode('list');
  }, [setSelectedClimateZones, setViewMode]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setIsShowingNearbyResults(false);
  }, [clearAllFilters]);

  // Handle filter apply (close popover)
  const handleFilterApply = useCallback(() => {
    setShowFilters(false);
    setPendingFilters(null);
  }, []);

  // Handle filter cancel (close popover without applying)
  const handleFilterCancel = useCallback(() => {
    setShowFilters(false);
    setPendingFilters(null);
  }, []);

  // Handle pending filter changes (for facet count updates)
  const handlePendingChange = useCallback((pending: PendingFilters) => {
    setPendingFilters(pending);
  }, []);

  const hasActiveFilters =
    searchText.trim() !== '' ||
    selectedCountry !== null ||
    selectedClimateZones.length > 0 ||
    selectedSourceTypes.length > 0 ||
    selectedYearRanges.length > 0;

  const activeFilterCount =
    (selectedCountry ? 1 : 0) +
    selectedClimateZones.length +
    selectedSourceTypes.length +
    selectedYearRanges.length;

  // Climate zone counts map for the grid view
  const climateZoneCountsMap = useMemo(
    () => new Map(manifestClimateZones.map((z) => [z.zone as ASHRAEClimateZone, z.count])),
    [manifestClimateZones]
  );

  // Climate zones - use dynamic facets when available, otherwise manifest counts
  const climateZones = useMemo((): { zone: ASHRAEClimateZone; count: number }[] => {
    const allZones: ASHRAEClimateZone[] = ['0A', '0B', '1A', '1B', '2A', '2B', '3A', '3B', '3C', '4A', '4B', '4C', '5A', '5B', '5C', '6A', '6B', '7', '7A', '7B', '8', '8A', '8B'];
    // Use dynamic facets if available (when filters are active), otherwise use manifest counts
    const sourceData = dynamicFacets?.climateZones ?? manifestClimateZones;
    const countMap = new Map(sourceData.map(z => [z.zone, z.count]));
    return allZones.map(zone => ({
      zone,
      count: countMap.get(zone) ?? 0,
    }));
  }, [manifestClimateZones, dynamicFacets]);

  // Source types - use dynamic facets when available, otherwise manifest counts
  const sourceTypes = useMemo((): { type: WeatherFileSourceType; count: number }[] => {
    const allTypes: WeatherFileSourceType[] = ['TMYx', 'TMY3', 'TMY2', 'IWEC', 'IWEC2', 'CWEC', 'CWEC2016', 'Other'];
    // Use dynamic facets if available (when filters are active), otherwise use manifest counts
    const sourceData = dynamicFacets?.sourceTypes ?? manifestSourceTypes;
    const countMap = new Map(sourceData.map(t => [t.type, t.count]));
    return allTypes.map(type => ({
      type,
      count: countMap.get(type) ?? 0,
    }));
  }, [manifestSourceTypes, dynamicFacets]);

  // Year ranges - use dynamic facets when available, otherwise manifest counts
  const yearRanges = useMemo((): { yearRange: string; count: number }[] => {
    // Start with known year ranges
    const knownRanges = ['2009-2023', '2007-2021', '2004-2018', 'none'];
    // Use dynamic facets if available (when filters are active), otherwise use manifest counts
    const sourceData = dynamicFacets?.yearRanges ?? manifestYearRanges;
    const countMap = new Map(sourceData.map(y => [y.yearRange, y.count]));

    // Get counts for known ranges
    const result = knownRanges.map(yearRange => ({
      yearRange,
      count: countMap.get(yearRange) ?? 0,
    }));

    // Add any other ranges from source data that we don't have
    for (const { yearRange, count } of sourceData) {
      if (!knownRanges.includes(yearRange)) {
        result.push({ yearRange, count });
      }
    }

    return result;
  }, [manifestYearRanges, dynamicFacets]);

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header - only show when used as modal/dialog */}
      {onClose && (
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-lg font-semibold">Weather File Library</h2>
            <p className="text-sm text-muted-foreground">
              Browse weather files from climate.onebuilding.org
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-muted"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Search bar and view toggle */}
      <div className="border-b px-4 py-3">
        <div className={`flex gap-2 ${isMobile ? 'flex-col' : 'flex-row items-center gap-4'}`}>
          <div className="flex-1">
            <WeatherFileSearch
              value={searchText}
              onChange={setSearchText}
              onUseLocation={handleUseLocation}
              isLocating={isLocating}
              isSearching={isLoadingResults}
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Filter toggle button */}
            {viewMode === 'list' && (
              <div className="relative" ref={!isMobile ? filterPopoverRef : undefined}>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
                    hasActiveFilters ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'
                  }`}
                  aria-label="Show filters"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Desktop filter popover */}
                {!isMobile && showFilters && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border bg-background p-4 shadow-lg">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="font-semibold">Filters</h3>
                      <button
                        onClick={handleFilterCancel}
                        className="rounded-md p-1 hover:bg-muted"
                        aria-label="Close filters"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 6 6 18" />
                          <path d="m6 6 12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                      <WeatherFilters
                        countries={countries}
                        climateZones={climateZones}
                        sourceTypes={sourceTypes}
                        yearRanges={yearRanges}
                        selectedCountry={selectedCountry}
                        selectedClimateZones={selectedClimateZones}
                        selectedSourceTypes={selectedSourceTypes}
                        selectedYearRanges={selectedYearRanges}
                        onCountryChange={setSelectedCountry}
                        onClimateZonesChange={setSelectedClimateZones}
                        onSourceTypesChange={setSelectedSourceTypes}
                        onYearRangesChange={setSelectedYearRanges}
                        onClearFilters={handleClearFilters}
                        hasActiveFilters={hasActiveFilters}
                        pendingMode={true}
                        onApply={handleFilterApply}
                        onCancel={handleFilterCancel}
                        onPendingChange={handlePendingChange}
                        pendingResultCount={pendingResultCount}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* View mode toggle */}
            <div className="flex rounded-md border" role="tablist" aria-label="View mode">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="List view"
                aria-selected={viewMode === 'list'}
                role="tab"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                <span className="hidden sm:inline">List</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'map'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="Map view"
                aria-selected={viewMode === 'map'}
                role="tab"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m3 7 6-3 6 3 6-3v13l-6 3-6-3-6 3V7" />
                  <path d="M9 4v13" />
                  <path d="M15 7v13" />
                </svg>
                <span className="hidden sm:inline">Map</span>
              </button>
              <button
                onClick={() => setViewMode('climate-zones')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'climate-zones'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="Climate zones view"
                aria-selected={viewMode === 'climate-zones'}
                role="tab"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                <span className="hidden sm:inline">Zones</span>
              </button>
            </div>
          </div>
        </div>

        {locationError && (
          <div
            className="mt-2 flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 dark:bg-red-950/30"
            role="alert"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0 text-red-500"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="flex-1 text-sm text-red-700 dark:text-red-300">{locationError}</span>
            <button
              onClick={() => {
                setLocationError(null);
                handleUseLocation();
              }}
              className="shrink-0 rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/30"
            >
              Retry
            </button>
            <button
              onClick={() => setLocationError(null)}
              className="shrink-0 rounded p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
              aria-label="Dismiss error"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Results area - full width now (no sidebar) */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className={`min-h-0 flex-1 ${viewMode === 'map' ? 'overflow-hidden p-0' : 'overflow-y-auto p-4'}`}
        >
          {viewMode === 'list' && (
            <>
              {!hasFilters && !isShowingNearbyResults ? (
                // Show prompt when no filters are selected and not showing nearby results
                <div className="space-y-8">
                  {/* Recent files section */}
                  {recentFiles.length > 0 && (
                    <div>
                      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        Recently Used
                      </h3>
                      <div className="space-y-2">
                        {recentFiles.map((file) => (
                          <button
                            key={file.id}
                            onClick={() => handleSelect(file)}
                            className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary ${
                              file.id === selectedFileId
                                ? 'border-primary bg-primary/5'
                                : ''
                            }`}
                          >
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                file.id === selectedFileId
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-primary/10 text-primary'
                              }`}
                            >
                              {file.id === selectedFileId ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">{file.location.city}</div>
                              <div className="truncate text-sm text-muted-foreground">
                                {file.location.region}, {file.location.country}
                              </div>
                            </div>
                            {file.climateZone.ashrae && (
                              <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                {file.climateZone.ashrae}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Search prompt */}
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mb-4 text-muted-foreground/50"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                    <h3 className="mb-2 text-lg font-medium">Search Weather Files</h3>
                    <p className="mb-4 max-w-sm text-sm text-muted-foreground">
                      Search by city name, select a country from the filters, or browse by climate zone
                      to find weather files from our library of 54,000+ locations worldwide.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <button
                        onClick={() => setSelectedCountry('US')}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        United States
                      </button>
                      <button
                        onClick={() => setSelectedCountry('CA')}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        Canada
                      </button>
                      <button
                        onClick={() => setSelectedCountry('GB')}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        United Kingdom
                      </button>
                      <button
                        onClick={() => setViewMode('climate-zones')}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        Browse by Climate Zone
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {isLoadingResults ? (
                        'Loading...'
                      ) : isShowingNearbyResults ? (
                        <>
                          {totalCount.toLocaleString()} nearby weather file{totalCount !== 1 ? 's' : ''} found
                        </>
                      ) : (
                        <>
                          {totalCount.toLocaleString()} weather file{totalCount !== 1 ? 's' : ''} found
                          {hasMore && ` (showing ${searchResults.length.toLocaleString()})`}
                        </>
                      )}
                    </p>
                  </div>
                  {isLoadingResults ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  ) : (
                    <>
                      <WeatherFileList
                        files={searchResults}
                        onSelect={handleSelect}
                        onPreview={handlePreview}
                        selectedFileId={selectedFileId}
                      />
                      {/* Loading more indicator */}
                      {isLoadingMore && (
                        <div className="flex items-center justify-center py-4" role="status" aria-live="polite">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
                          <span className="ml-2 text-sm text-muted-foreground">Loading more...</span>
                        </div>
                      )}
                      {/* Load more button and hint */}
                      {hasMore && !isLoadingMore && (
                        <div className="flex flex-col items-center gap-2 py-4">
                          <button
                            onClick={loadMore}
                            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
                          >
                            Load More Results
                          </button>
                          <span className="text-xs text-muted-foreground">
                            or scroll down to load automatically
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {viewMode === 'map' && (
            <WeatherFileMap
              onSelect={handleSelect}
              onPreview={handlePreview}
              selectedFile={previewFile}
              searchQuery={searchQuery}
            />
          )}

          {viewMode === 'climate-zones' && (
            <ClimateZoneGrid
              onSelect={handleClimateZoneSelect}
              zoneCounts={climateZoneCountsMap}
            />
          )}
        </div>
      </div>

      {/* Mobile filters drawer */}
      {isMobile && showFilters && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleFilterCancel}
          />
          {/* Drawer - account for bottom nav bar height */}
          <div className="relative ml-auto h-full w-80 max-w-[85vw] overflow-y-auto bg-background pb-12 shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b bg-background px-4 py-3">
              <h3 className="font-semibold">Filters</h3>
              <button
                onClick={handleFilterCancel}
                className="rounded-md p-1.5 hover:bg-muted"
                aria-label="Close filters"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <WeatherFilters
                countries={countries}
                climateZones={climateZones}
                sourceTypes={sourceTypes}
                yearRanges={yearRanges}
                selectedCountry={selectedCountry}
                selectedClimateZones={selectedClimateZones}
                selectedSourceTypes={selectedSourceTypes}
                selectedYearRanges={selectedYearRanges}
                onCountryChange={setSelectedCountry}
                onClimateZonesChange={setSelectedClimateZones}
                onSourceTypesChange={setSelectedSourceTypes}
                onYearRangesChange={setSelectedYearRanges}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                pendingMode={true}
                onApply={handleFilterApply}
                onCancel={handleFilterCancel}
                onPendingChange={handlePendingChange}
                pendingResultCount={pendingResultCount}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewFile && (
        <WeatherFilePreview
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onSelect={() => {
            handleSelect(previewFile);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
}
